import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { api, ApiError } from '../api';
import { useAuth, type User } from './AuthProvider';
import { AppIcon } from '../theme/icons';
import { TEAM_OPTIONS } from '../theme/teamPalette';

export default function RegisterPage() {
  const [params] = useSearchParams();
  const [inviteCode, setInviteCode] = useState(params.get('code') ?? '');
  const [username, setUsername] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [password, setPassword] = useState('');
  const [favoriteTeam, setFavoriteTeam] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();
  const { login } = useAuth();

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const res = await api<{ token: string; user: User }>('/api/auth/register', {
        method: 'POST',
        body: {
          inviteCode: inviteCode.trim(),
          username: username.trim(),
          displayName: displayName.trim(),
          password,
          favoriteTeam: favoriteTeam || undefined,
        },
      });
      login(res.token, res.user);
      navigate('/albums', { replace: true });
    } catch (err) {
      if (err instanceof ApiError) {
        const code = (err.data as { error?: string } | null)?.error;
        if (code === 'invalid_invite_code') setError('Einladungscode unbekannt');
        else if (code === 'invite_code_already_used') setError('Code wurde schon eingelöst');
        else if (code === 'username_taken') setError('Benutzername schon vergeben');
        else if (code === 'invalid_body') setError('Bitte alles ausfüllen (Passwort ≥ 8 Zeichen)');
        else setError('Registrierung fehlgeschlagen');
      } else {
        setError('Netzwerkfehler — versuch es nochmal');
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="min-h-full flex flex-col px-6 pt-8 safe-top safe-bottom relative z-[1]">
      <AppIcon size={48} gradId="dkbg-register" />
      <h1 className="font-sans text-[26px] font-extrabold tracking-[-.02em] text-text leading-tight mt-5">
        Einladungscode einlösen
      </h1>
      <p className="font-sans text-[14px] text-muted mt-1.5 mb-6">
        Du brauchst einen Code aus dem Freundeskreis.
      </p>

      <form onSubmit={onSubmit} className="space-y-3.5">
        <Field label="EINLADUNGSCODE">
          <input
            value={inviteCode}
            onChange={(e) => setInviteCode(e.target.value)}
            required
            className="w-full px-4 py-3.5 rounded-[13px] bg-surface border border-border text-text font-mono uppercase tracking-wider text-[14px] outline-none focus:border-primary focus:ring-2 focus:ring-primary/30"
          />
        </Field>
        <Field label="BENUTZERNAME">
          <input
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            autoComplete="username"
            pattern="[a-zA-Z0-9_-]+"
            minLength={3}
            required
            className="w-full px-4 py-3.5 rounded-[13px] bg-surface border border-border text-text font-sans text-[14px] outline-none focus:border-primary focus:ring-2 focus:ring-primary/30"
          />
        </Field>
        <Field label="ANZEIGENAME">
          <input
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            required
            className="w-full px-4 py-3.5 rounded-[13px] bg-surface border border-border text-text font-sans text-[14px] outline-none focus:border-primary focus:ring-2 focus:ring-primary/30"
          />
        </Field>
        <Field label="PASSWORT (MIN. 8 ZEICHEN)">
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="new-password"
            minLength={8}
            required
            className="w-full px-4 py-3.5 rounded-[13px] bg-surface border border-border text-text font-mono text-[14px] outline-none focus:border-primary focus:ring-2 focus:ring-primary/30"
          />
        </Field>
        <Field label="LIEBLINGSTEAM (OPTIONAL, FÜRS THEMING)">
          <select
            value={favoriteTeam}
            onChange={(e) => setFavoriteTeam(e.target.value)}
            className="w-full px-4 py-3.5 rounded-[13px] bg-surface border border-border text-text font-sans text-[14px] outline-none focus:border-primary focus:ring-2 focus:ring-primary/30"
          >
            <option value="">— kein Team —</option>
            {TEAM_OPTIONS.map((t) => (
              <option key={t.code} value={t.code}>{t.label}</option>
            ))}
          </select>
        </Field>

        {error && <p className="font-sans text-[13px] text-status-double">{error}</p>}

        <button
          type="submit"
          disabled={busy}
          className="w-full py-[15px] rounded-[14px] bg-primary text-white font-sans font-bold text-[15px] disabled:opacity-50"
        >
          {busy ? 'Lege an…' : 'Account anlegen'}
        </button>
      </form>

      <Link
        to="/login"
        className="block text-center font-sans font-semibold text-[13px] text-accent mt-5 mb-2"
      >
        Schon Account? Anmelden
      </Link>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="block font-mono text-[11px] font-semibold tracking-[.06em] text-muted mb-[7px]">
        {label}
      </span>
      {children}
    </label>
  );
}
